import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { matchPath, useLocation } from 'react-router-dom';
import { useIsMobile } from '../../../hooks/useIsMobile';

const routeComponentMap: [string, string][] = [
  ['/', 'MobileHome.tsx'],
  ['/home', 'MobileHome.tsx'],
  ['/about', 'MobileAboutUs.tsx'],
  ['/magazines', 'MobileMagazines.tsx'],
  ['/magazines/vivekvani', 'mVivekvaniListPage.tsx'],
  ['/magazines/kalyan/category/:catid/:kalyanid', 'mMagazineHomePage.tsx'],
  ['/magazines/kalyan/category/:catid', 'mKalyanListPage.tsx'],
  ['/magazines/kalyan/:slug', 'mMagazineHomePage.tsx'],
  ['/magazines/kalyan', 'mKalyanRoute.tsx'],
  ['/magazines/kalyanakalpataru/category/:catid/:kalyanid', 'mMagazineHomePage.tsx'],
  ['/magazines/kalyanakalpataru/category/:catid', 'mKalpataruListPage.tsx'],
  ['/magazines/kalyanakalpataru/:slug', 'mMagazineHomePage.tsx'],
  ['/magazines/kalyanakalpataru', 'mKalpataruRoute.tsx'],
  ['/magazines/geetgovind/category/:catid', 'mGeetGovindListPage.tsx'],
  ['/magazines/geetgovind/author/:id', 'mGeetGovindListPage.tsx'],
  ['/magazines/geetgovind/language/:langid', 'mGeetGovindListPage.tsx'],
  ['/magazines/geetgovind/lang/:lang', 'mGeetGovindListPage.tsx'],
  ['/magazines/geetgovind/:slug', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind/category/:catid/:id', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind/language/:langid/:id', 'MBookDetailPage.tsx'],
  ['/magazines/geetgovind', 'mGeetGovindRoute.tsx'],
  ['/books', 'MobileBooksRoute.tsx'],
  ['/books/author/:id', 'MobileBooksRoute.tsx'],
  ['/books/language/:langid', 'MobileBooksRoute.tsx'],
  ['/books/category/:catid', 'MobileBooksRoute.tsx'],
  ['/books/:bookid', 'mBookDetailPage.tsx'],
  ['/reader/:type/:id', 'MobileEpub.tsx'],
  ['/pravachans', 'mPravachanHubPage.tsx / MobileAudioListPage.tsx'],
  ['/pravachans/author/:id', 'mPravachanHubPage.tsx'],
  ['/pravachans/:id', 'AudiosDetailPage.tsx'],
  ['/audios', 'MbAudioCategory.tsx / MobileAudioListPage.tsx'],
  ['/audios/author/:id', 'MobileAudioListPage.tsx'],
  ['/audios/:slug', 'MobileAudioListPage.tsx'],
  ['/playlist/:slug', 'MobileAudioPlayerRemoved (download)'],
  ['/DailySatsang', 'DailySatsangPage.tsx / MobileAudioListPage.tsx'],
  ['/DailySatsang/:slug', 'MobileAudioListPage.tsx'],
  ['/videos', 'MbAllVideos.tsx'],
  ['/videos/author/:id', 'MbAllVideos.tsx'],
  ['/videos/category/:id', 'MbAllVideos.tsx'],
  ['/videos/video/:id', 'MbAllVideos.tsx'],
  ['/articles', 'mArticlesPage.tsx'],
  ['/articles/author/:id', 'mArticlesPage.tsx'],
  ['/articles/category/:catid', 'mArticlesPage.tsx'],
  ['/articles/language/:langid', 'mArticlesPage.tsx'],
  ['/articles/:slug', 'ReadArticles.tsx'],
  ['/divinequotes', 'MobileDivineQuotes.tsx'],
  ['/divinequotes/:id', 'mDivineQuotes.tsx'],
  ['/privacypolicy', 'MobilePrivacyPolicy.tsx'],
  ['/termsofuse', 'MobileTermsOfUse.tsx'],
  ['/filter', 'MobileBooksFilterPage.tsx'],
  ['/myFavourite', 'MobileFavoriatePage.tsx'],
  ['/myProfile', 'MobileProfilePage.tsx'],
  ['/divineQuotes', 'MobileDivineQuotes.tsx'],
  ['/divineQuotesImg', 'DivineQuotesImageview.tsx'],
  ['/subjectData', 'FilteredSubjectData.tsx'],
  ['/recentlyViewed', 'MobileRecentlyViewed.tsx'],
  ['/readArticles', 'ReadArticles.tsx'],
  ['/loginPage', 'MobileLoginPage.tsx'],
  ['/mobileAudioListPage', 'MobileAudioListPage.tsx'],
  ['/categoriesFilter', 'CategoriesFilter.tsx'],
  ['/audioPlayerPage', 'MobileAudioPlayerRemoved (download)'],
  ['/mbAudioCategory', 'MbAudioCategory.tsx'],
  ['/mobileArticles', 'redirect /articles'],
  ['/terms-of-use', 'MobileTermsOfUse.tsx'],
  ['/privacy-policy', 'MobilePrivacyPolicy.tsx'],
  ['/publicity', 'MobilePromotion.tsx'],
  ['/pamphletEng', 'MobileEnglishTemplate.tsx'],
  ['/pamphletHindi', 'MobileHindiTemplate.tsx'],
  ['/author/:id', 'mAuthorDetailsPage.tsx'],
  ['/profile/', 'MobileProfilePage.tsx'],
  ['/profile/My-Favourites', 'MobileFavoriatePage.tsx'],
  ['/Login/*', 'MobileLoginPage.tsx'],
  ['/webView', 'WebViewforbook.tsx'],
  ['/fullimage', 'FullImage.tsx'],
];

function getPageFile(pathname: string): string {
  for (const [pattern, component] of routeComponentMap) {
    if (matchPath(pattern, pathname)) {
      return component;
    }
  }
  return '—';
}

/**
 * Development-only fixed bar showing the current mobile route and component file name.
 * Hidden on desktop and stripped from production builds via NODE_ENV.
 */
const MobileDevPageInfoBar: React.FC = () => {
    const location = useLocation();
    const isMobile = useIsMobile();
    const isDev = process.env.NODE_ENV === 'development';
    const pathLabel = `${location.pathname}${location.search || ''}`;
    const shouldShow = isDev && isMobile;
    const pageFile = useMemo(() => getPageFile(location.pathname), [location.pathname]);
    // Session-only dismiss — resets on full page refresh (no storage)
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => {
        if (!shouldShow || isDismissed) return;
        const logdetails: string[] = [];
        logdetails.push('dev_page_info_bar_visible');
        logdetails.push(`path=${pathLabel}`);
        logdetails.push(`component=${pageFile}`);
        console.log('[MobileDevPageInfoBar]', logdetails.join(' | '));
    }, [shouldShow, isDismissed, pathLabel, pageFile]);

    /** Hide bar for current SPA session; reappears after refresh */
    const handleDismiss = () => {
        const logdetails: string[] = [];
        logdetails.push('action=dismiss_dev_page_info_bar');
        logdetails.push(`path=${pathLabel}`);
        logdetails.push(`component=${pageFile}`);
        console.log('[MobileDevPageInfoBar]', logdetails.join(' | '));
        setIsDismissed(true);
    };

    if (!shouldShow || isDismissed) {
        return null;
    }

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '40px',
                backgroundColor: '#f97316',
                color: '#000000',
                position: 'fixed',
                bottom: 0,
                left: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: 1,
                py: 0.5,
                pl: 5,
                boxSizing: 'border-box',
                pointerEvents: 'none',
            }}
            role="status"
            aria-label={`Development page info: ${pageFile}`}
        >
            <Box
                component="span"
                sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: 'chanakyaUni, sans-serif',
                    textAlign: 'left',
                    wordBreak: 'break-all',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                }}
            >
                {pageFile} | {pathLabel}
            </Box>
            <IconButton
                aria-label="Close development page info bar"
                onClick={handleDismiss}
                size="small"
                sx={{
                    position: 'absolute',
                    left: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#000000',
                    pointerEvents: 'auto',
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

export default MobileDevPageInfoBar;
